import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPaymentTypesList } from '../store/paymentTypes-actions';
import { fetchCampaignsList, fetchSubCampaignsList } from '../store/campaigns-actions'
import { fetchOrganizationsList } from '../store/organizations-actions'
import { fetchWebSettings, fetchDonorSettings } from '../store/settings-actions';
import { fetchLocationsList } from '../store/locations-actions';
import { fetchAllTendersLists } from '../store/tenders-actions';
import { fetchParentStudentsList } from '../store/students-actions';
import { fetchSolicitorsList } from '../store/solicitors-actions';
import { fetchAccountsList } from '../store/accounts-actions';
import { fetchShulsList } from '../store/shuls-actions';
import { fetchSchoolYearsList, fetchSchoolTypesList } from '../store/school-actions';
import { isAppPagePath } from '../functions/isAppPagePath';

// This hook fetches GLOBAL data that's used across multiple pages
// Individual lists are fetched lazily by their respective components on first visit
const useFetchData = () => {
  const dispatch = useDispatch();

  // Global data selectors (used across multiple pages)
  const campaigns = useSelector((state) => state.campaigns.campaigns)
  const subCampaigns = useSelector((state) => state.campaigns.subCampaigns)
  const organizations = useSelector((state) => state.organizations.organizations)
  const locations = useSelector((state) => state.locations.locations)
  const paymentTypes = useSelector((state) => state.paymentTypes.paymentTypes)
  const solicitors = useSelector((state) => state.solicitors.solicitors)
  const accounts = useSelector((state) => state.accounts.accounts)
  const shuls = useSelector((state) => state.shuls.shuls)
  const loggedIn = useSelector((state) => state.auth.isLoggedIn)
  const parentLoggedIn = useSelector((state) => state.auth.parentLoggedIn)
  const hubUserLoggedIn = useSelector((state) => state.auth.hubUserLoggedIn)
  const donorLoggedIn = useSelector((state) => state.auth.donorLoggedIn)
  const settingsLoaded = useSelector((state) => state.settings.settingsLoaded)
  const loadedStudents = useSelector((state) => state.students.loadedStudents)
  const visibleTabs = useSelector((state) => state.auth.permissions.VisibleTabs)

  const schoolYears = useSelector((state) => state.school.schoolYears)
  const schoolTypes = useSelector((state) => state.school.schoolTypes)

  // School is considered enabled if the user has access to the School tab (same check as sidebar nav)
  const schoolEnabled = !!(visibleTabs && visibleTabs.includes('School'));

  // Fetch campaigns (used in filters across many pages)
  useEffect(() => {
    if (campaigns.length === 0 && (loggedIn || hubUserLoggedIn)) {
      dispatch(fetchCampaignsList())
    }
  }, [dispatch, campaigns.length, loggedIn, hubUserLoggedIn])

  // Fetch sub-campaigns (used in filters across many pages)
  useEffect(() => {
    if (subCampaigns.length === 0 && loggedIn && ! window.location.href.includes('-app')) {
      dispatch(fetchSubCampaignsList())
    }
  }, [dispatch, subCampaigns.length, loggedIn])

  // Fetch organizations (used in filters/dropdowns)
  useEffect(() => {
    if (organizations.length === 0 && loggedIn && ! window.location.href.includes('-app')) {
      dispatch(fetchOrganizationsList())
    }
  }, [dispatch, organizations.length, loggedIn])

  // Fetch locations (used in filters/dropdowns)
  useEffect(() => {
    if (locations.length === 0 && loggedIn && ! window.location.href.includes('-app')) {
      dispatch(fetchLocationsList())
    }
  }, [dispatch, locations.length, loggedIn])

  // Fetch payment types (used everywhere)
  useEffect(() => {
    if (paymentTypes.length === 0 && (loggedIn || parentLoggedIn || donorLoggedIn) && ! window.location.href.includes('-app')) {
      dispatch(fetchPaymentTypesList())
    }
  }, [dispatch, paymentTypes.length, loggedIn, parentLoggedIn, donorLoggedIn])

  // Fetch web settings (global app settings)
  useEffect(()=>{
    if(!settingsLoaded && loggedIn && ! window.location.href.includes('-app')){
      dispatch(fetchWebSettings())
    }
  },[dispatch, settingsLoaded, loggedIn])
  
  // Fetch donor settings (for donor portal)
  useEffect(()=>{
    if(!settingsLoaded && donorLoggedIn && ! window.location.href.includes('-app')){
      dispatch(fetchDonorSettings())
    }
  },[dispatch, settingsLoaded, donorLoggedIn])

  // Fetch tenders for donor portal (donor-specific)
  useEffect(() => {
    if (donorLoggedIn && ! window.location.href.includes('-app')) {
      dispatch(fetchAllTendersLists(0))
    }
  }, [dispatch, donorLoggedIn])

  // Fetch parent's students (parent-specific)
  useEffect(()=>{
    if(parentLoggedIn && !loadedStudents && ! window.location.href.includes('-app')){
      dispatch(fetchParentStudentsList())
    }
  },[parentLoggedIn, loadedStudents, dispatch])

  // Fetch solicitors (used in payment forms/dropdowns)
  useEffect(() => {
    if (solicitors.length === 0 && loggedIn && !window.location.href.includes('-app')) {
      dispatch(fetchSolicitorsList())
    }
  }, [dispatch, solicitors.length, loggedIn])

  // Fetch accounts (used in payment forms/dropdowns)
  useEffect(() => {
    if (accounts.length === 0 && loggedIn && !window.location.href.includes('-app')) {
      dispatch(fetchAccountsList())
    }
  }, [dispatch, accounts.length, loggedIn])

  // Fetch shuls (used in payment forms/dropdowns)
  useEffect(() => {
    if (shuls.length === 0 && loggedIn && !window.location.href.includes('-app')) {
      dispatch(fetchShulsList())
    }
  }, [dispatch, shuls.length, loggedIn])

  // Fetch school years & school types (used for resolving IDs to display strings)
  useEffect(() => {
    // Same check as SchoolContextControls, which loads the years itself on app pages.
    if (!schoolEnabled || !loggedIn || isAppPagePath(window.location.pathname)) {
      return;
    }

    if (schoolYears.length === 0) {
      dispatch(fetchSchoolYearsList());
    }

    if (schoolTypes.length === 0) {
      dispatch(fetchSchoolTypesList());
    }
  }, [
    dispatch,
    schoolEnabled,
    loggedIn,
    schoolYears.length,
    schoolTypes.length,
  ])

  // NOTE: List fetching removed from here - now handled by individual components on first visit:
  // - Donors list → DonorsForm (lazy)
  // - Payments list → PaymentsForm (lazy)
  // - Recurring Payments → RecurringPaymentsForm (lazy)
  // - Pledges list → PledgesForm (lazy)
  // - Recurring Pledges → RecurringPledgesForm (lazy)
  // - Parents list → ParentsForm (lazy)
  // - Students list → StudentsForm (lazy)
  //
  // This provides optimal performance - only fetch data when user actually visits the page

};

export default useFetchData;
